import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ParadigmaModal from "../../../components/ParadigmaModal/ParadigmaModal.js";
import TelefonoQR from '../../../../public/img/telefono-QR.svg';
import { Context } from '../constants.js';

/**
 * ModalQRCuotaScanner
 * Esta componente inicialmente se ve como un botón, al presionarlo se abre el
 * modal para escanear QR.
 *
 * Tiene un uso distinto a los demas modales del proyecto ya que su proposito
 * es delegarle un contexto_id + cuota_id extraido de un código QR al ModalCuotas.
 * Donde contexto_id es el id que use el ModalCuotas, por ejemplo, idServicio o
 * idVenta, ya que estan dentro del contexto de cuotas de Servicios o de Ventas.
 */
class ModalQRCuotaScanner extends Component {
    DEFAULT_SCANNED_CONTEXT_ID_AND_CUOTA_ID = '';

    constructor(props) {
        super(props);
        this.state = {
            isModalPagarCuotasOpen: false,
            scannedContextIdAndCuotaId: this.DEFAULT_SCANNED_CONTEXT_ID_AND_CUOTA_ID,
            inputServicioIdRef: React.createRef(),
            inputDisabled: false,
            invalidContextErrorMessage: '',
        }
    }

    static propTypes = {
        // Modificar ModalCuotas para que acepte las props que se le inyectan.
        ModalCuotasProp: PropTypes.elementType.isRequired,
        context: PropTypes.oneOf(Object.values(Context)).isRequired,
        cuotaIdPropName: PropTypes.string.isRequired,
        // El nombre del contexto es el que use el ModalCuotas pasado por prop,
        // Ej, "idServicio", "idVenta", "idEscritura"
        contextIdPropName: PropTypes.string.isRequired,
        title: PropTypes.string,
    }

    focusInputServicioId = () => {
        const currentInputRef = this.state.inputServicioIdRef.current;
        if (currentInputRef) {
            currentInputRef.focus();
        }
    };

    /**
     * Al cerrarse el modal de cuotas, se tiene que desactivar la apertura automatica del ParadigmaModal
     */
    closeModalPagarCuotas = () => {
        // Se hace reset del state
        this.setState({
            isModalPagarCuotasOpen: false,
            scannedContextIdAndCuotaId: this.DEFAULT_SCANNED_CONTEXT_ID_AND_CUOTA_ID,
            inputDisabled: false,
            invalidContextErrorMessage: '',
        })
    };

    openModalPagarCuotas = () => {
        this.setState ({
            isModalPagarCuotasOpen: true,
            inputDisabled: true,
        });
    };

    handleInputChange = (event) => {
        const value = event.target.value;

        // El formato es string-numero-numero,
        // notar que estan separados por guiones (-)
        // Ej, servicio-1234-789
        if (/^[a-zA-Z]*(-[0-9]*)?(-[0-9]*)?$/.test(value)) {
            this.setState({ scannedContextIdAndCuotaId: value });
        }
    };

    handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            this.openModalPagarCuotas();
        }
    };

    isScannedInputValid = (value) => {
        return /^[a-zA-Z]+-[0-9]+-[0-9]+$/.test(value)
    }

    getScannedContextId = () => {
        const { scannedContextIdAndCuotaId } = this.state;
        let scannedContextId = false

        if (this.isScannedInputValid(scannedContextIdAndCuotaId)){
            const [, contextId] = scannedContextIdAndCuotaId.split('-');
            scannedContextId = Number(contextId)
        }

        return scannedContextId
    };

    capitalize = (str) => {
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    /**
     * Medida de seguridad para no pagar una cuota de un contexto que no
     * corresponde.
     * Se evita que, por ejemplo, se intente pagar un 'Servicio' cuando
     * actualmente se está en la pestaña de 'Ventas'.
     *
     * Muestra mensaje de error de contexto en en el modal en caso de que suceda.
     */
    isContextValid = (scannedContextIdAndCuotaId) => {
        const { context } = this.props;

        const [scannedContext] = scannedContextIdAndCuotaId.split('-');
        let isContextValid = (scannedContext.toLowerCase() == context.toLowerCase())

        if (!isContextValid){
            this.setState({ invalidContextErrorMessage: `No se puede pagar una cuota de '${this.capitalize(scannedContext)}' estando en la pestaña de '${this.capitalize(context)}'.` });
        }

        return isContextValid
    };

    getScannedCuotaId = () => {
        const { scannedContextIdAndCuotaId } = this.state;
        // Para que el ModalCuotas de error de que falta id hay que poner el id en false
        let scannedCuotaId = false

        if (this.isScannedInputValid(scannedContextIdAndCuotaId) && this.isContextValid(scannedContextIdAndCuotaId)){
            // handleInputChange() asegura que scannedContextIdAndCuotaId
            // tenga formato correcto
            const [, , cuotaId] = scannedContextIdAndCuotaId.split('-');
            scannedCuotaId = cuotaId ? Number(cuotaId) : false
        }

        return scannedCuotaId
    };

    render() {
        const {
            scannedContextIdAndCuotaId,
            inputServicioIdRef,
            inputDisabled,
            isModalPagarCuotasOpen,
            invalidContextErrorMessage,
        } = this.state;

        const {
            ModalCuotasProp,
            context,
            cuotaIdPropName,
            contextIdPropName,
            title,
        } = this.props;

        const modalCuotasProps = {
            ...this.props,
            [cuotaIdPropName]: this.getScannedCuotaId,
            [contextIdPropName]: this.getScannedContextId,
            initOpen: isModalPagarCuotasOpen,
            onClose: this.closeModalPagarCuotas,
            action: "PAGAR",
            activeTab: 2,
            missingIdMessage:  invalidContextErrorMessage ? invalidContextErrorMessage : "No se ingresó un código con formato correcto.",
        };

        const titleParadigmaModal = title ? title : "Escanear QR para cobro de cuota";

        return (
            <ParadigmaModal
                onVisible={() => this.focusInputServicioId()}
                onClose={this.closeModalPagarCuotas}

                title={titleParadigmaModal}

                buttonTitle="Pagar cuota con código QR"
                buttonIcon="fa fa-qrcode fa-lg"
                buttonClass=""

                closeButton={true}
                cancelButtonLabel="Cancelar"
                className="ui-paradigma-modal"
            >
                <div className="ui-qr-scanner">
                    <section className="ui-modal-section">
                        <p className="ui-section-label">Código QR</p>
                        <div className="ui-qr-scanner-content">
                            <img src={TelefonoQR} alt="Telefono QR" className="ui-qr-scanner-image" />
                            <label htmlFor="scannedContextIdAndCuotaId">{`Cuota de ${context} escaneada`}</label>
                            <div className="ui-qr-scanner-control">
                                <input
                                    className="form-control"
                                    disabled={inputDisabled} // Se deshabilita para que el input no siga en focus mientras se usa el modal cuotas
                                    ref={inputServicioIdRef}
                                    type="text"
                                    id="scannedContextIdAndCuotaId"
                                    value={scannedContextIdAndCuotaId}
                                    onChange={this.handleInputChange}
                                    onKeyDown={this.handleKeyDown}
                                    onBlur={e => e.target.focus()} // Se fuerza a mantener al input siempre en focus no importa donde se haga click
                                    placeholder="Escanee un código QR..."
                                />
                                {/* Boton que abre el modal cuotas con el ID del servicio en el input */}
                                <button onClick={this.openModalPagarCuotas} className="ui-qr-scanner-button" type="button">
                                    <i className="fa fa-search" aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* ModalCuotas se ve como un botón inicialmente, por lo que hay que esconderlo sin desactivar el componente en si. */}
                    <div className="ui-visually-hidden">
                        <ModalCuotasProp {...modalCuotasProps} />
                    </div>
                </div>
            </ParadigmaModal>
        );
    }
}

export default ModalQRCuotaScanner;




// WEBPACK FOOTER //
// ./src/components/Pagar/ModalQRCuotaScanner/ModalQRCuotaScanner.js